/* eslint-disable react/no-unknown-property */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {
  useContext, useEffect, useState, useMemo, useRef
} from 'react'
import {
  CardInfo, Icon, Input, Link, Spinner, Typography
} from '@telus/koodo-ui-kit'
import { ApplicationContext, APPLICATION_STATE } from '@apps/contexts/ApplicationContext'
import { PHONE_NUMBER_STATE, updatePhoneNumberState } from '@apps/redux/features/PhoneNumberSlice'
import { useDispatch, useSelector } from 'react-redux'
import { getById, getBySlug } from '@apps/utils/contentful'
import Sites from '@sites/index'
import BodyError from '../Typography/BodyError'

const getPhoneNumberInFormat = (value) => {
  if (!value) return ''
  // country code
  if (value.length === 11) {
    return `(${value?.substring(1, 4)}) ${value?.substring(4, 7)} - ${value?.substring(7)}`
  }
  return `(${value?.substring(0, 3)}) ${value?.substring(3, 6)} - ${value?.substring(6)}`
}

const MAX_CYCLES = 10
const CYCLE_AMOUNT = 3

const CreateNewNumber = ({ selectedNum, pushClickAnalytics }) => {
  const dispatch = useDispatch()
  const { userType } = useSelector((state) => state.common)
  const { pageContent } = useSelector((state) => state.common.content)

  const [showMore, setShowMore] = useState(true)
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('')
  const [currentNumber, setCurrentNumber] = useState(selectedNum)
  const [totalNumbers, setTotalNumbers] = useState([])
  const [reservedNumbers, setReservedNumbers] = useState([])
  const [isNumberLoading, setIsNumberLoading] = useState(false)
  const [activePhoneNumbers, setActivePhoneNumbers] = useState([])
  const [cycle, setCycle] = useState(1)
  const [reservedError, setReservedError] = useState('')
  const [isSetPhoneNumberLoading, setIsSetPhoneNumberLoading] = useState(false)
  // prevent duplicate setPhoneNumber API calls on rapid clicks
  const settingPhoneNumberRef = useRef(false)

  const {
    [APPLICATION_STATE.SERVICES]: brandServices,
    [APPLICATION_STATE.CURRENT_LANGUAGE]: language
  } = useContext(ApplicationContext)

  const { getPhoneNumbers, setPhoneNumber } = brandServices

  const pagePrefix = userType === Sites.dealer ? 'Dealer' : 'Consumer'
  const phoneNumberPageContent = getById(pageContent, `phoneNumberPage${pagePrefix}`)[language]
  const { shortTextFields } = phoneNumberPageContent || {}
  const textContent = useMemo(() => {
    return {
      newNumber: getBySlug(shortTextFields, 'newNumber').value,
      seeMore: getBySlug(shortTextFields, 'seeMore').value,
      refresh: getBySlug(shortTextFields, 'refresh').value,
      noNumber: getBySlug(shortTextFields, 'noNumber').value,
      reservedNumber: getBySlug(shortTextFields, 'reservedNumber').value
    }
  }, [shortTextFields])
  const {
    newNumber,
    seeMore,
    refresh,
    noNumber,
    reservedNumber: reservedNumberError
  } = textContent

  const phoneNumberState = useSelector((state) => state.phoneNumberPage)

  const { [PHONE_NUMBER_STATE.CREATE_NEW_NUMBER.PROVINCE_GROUP_ID]: provinceGroupId } =
    phoneNumberState

  const updatePhoneNumberStore = (payload) => {
    dispatch(updatePhoneNumberState(payload))
  }

  const getNumberOptions = () => {
    let cycleIndex = (cycle - 1) * CYCLE_AMOUNT

    if (cycle <= MAX_CYCLES && provinceGroupId && totalNumbers.length < MAX_CYCLES * CYCLE_AMOUNT) {
      setIsNumberLoading(true)
      getPhoneNumbers(provinceGroupId, CYCLE_AMOUNT).then((response) => {
        setIsNumberLoading(false)
        if (!response) {
          setCycle(1)
          return
        }
        const newTotal = [...totalNumbers, ...response]
        setTotalNumbers(newTotal)
        setActivePhoneNumbers(newTotal.slice(cycleIndex, cycleIndex + CYCLE_AMOUNT))
      })
    } else {
      if (cycle > MAX_CYCLES) {
        setCycle(1)
        cycleIndex = 0
      }
      setActivePhoneNumbers(totalNumbers.slice(cycleIndex, cycleIndex + CYCLE_AMOUNT))
    }
  }

  const formatNumberToPort = (rawNumber) => {
    let number

    if (/^\d+$/.test(rawNumber)) {
      if (rawNumber && rawNumber.length >= 11 && rawNumber.startsWith('1')) {
        number = rawNumber
      } else if (rawNumber && rawNumber.length >= 10 && rawNumber.length <= 11) {
        number = `1${rawNumber}`
      }
    }

    return number
  }

  useEffect(() => {
    if (cycle > 1) {
      getNumberOptions()
    }
  }, [cycle])

  const getRefreshOptions = () => {
    setCycle(cycle + 1)
    setActivePhoneNumbers([])
  }

  const handleSeeMore = () => {
    if (pushClickAnalytics) {
      const eventType = showMore ? seeMore : refresh
      pushClickAnalytics(eventType, eventType)
    }
    if (showMore) {
      setShowMore(false)
      getNumberOptions()
    } else {
      setSelectedPhoneNumber('')
      getRefreshOptions()
    }
  }

  const handleSeeMoreClick = (event) => {
    if (event?.preventDefault) {
      event.preventDefault()
    }
    handleSeeMore()
  }

  const handleSeeMoreKeyDown = (event) => {
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      handleSeeMore()
    }
  }

  useEffect(() => {
    setCurrentNumber(selectedNum)
    updatePhoneNumberStore({
      [PHONE_NUMBER_STATE.CREATE_NEW_NUMBER.PHONE_NUMBER]: selectedNum,
      [PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER]: formatNumberToPort(selectedNum?.value)
    })
  }, [selectedNum])

  useEffect(() => {
    if (selectedPhoneNumber !== '' && activePhoneNumbers.length > 0 && activePhoneNumbers[selectedPhoneNumber]) {
      setCurrentNumber(activePhoneNumbers[selectedPhoneNumber])
      updatePhoneNumberStore({
        [PHONE_NUMBER_STATE.CREATE_NEW_NUMBER.PHONE_NUMBER]:
          activePhoneNumbers[selectedPhoneNumber],
        [PHONE_NUMBER_STATE.SELECTED_PHONE_NUMBER]: formatNumberToPort(
          activePhoneNumbers[selectedPhoneNumber].value
        )
      })
    }
  }, [selectedPhoneNumber, activePhoneNumbers])

  useEffect(() => {
    if (reservedError) {
      setReservedError(reservedNumberError)
    }
  }, [language])

  const handleNumberSelect = async (index) => {
    // Ignore re-entry while a previous setPhoneNumber call is in-progress
    if (settingPhoneNumberRef.current) {
      return
    }
    const found = activePhoneNumbers[index]
    if (!reservedNumbers.includes(found)) {
      setIsSetPhoneNumberLoading(true)
      settingPhoneNumberRef.current = true
      const isReserved = await setPhoneNumber(found)
      if (!isReserved) {
        setReservedError(reservedNumberError)
        setIsSetPhoneNumberLoading(false)
        settingPhoneNumberRef.current = false
        return
      }
    }
    setReservedError('')
    setIsSetPhoneNumberLoading(false)
    settingPhoneNumberRef.current = false
    setReservedNumbers([...reservedNumbers, found])
    setSelectedPhoneNumber(index)
  }

  const phoneNumberOptions = activePhoneNumbers.map((number) => ({
    title: getPhoneNumberInFormat(number.value),
    value: number.value
  }))

  const selectedPhoneNumberValue =
    activePhoneNumbers[selectedPhoneNumber]?.value || currentNumber?.value

  const handlePhoneNumberChange = (selectedValue) => {
    const index = activePhoneNumbers.findIndex((number) => number.value === selectedValue)
    if (index === -1) return
    handleNumberSelect(index)
  }

  return (
    <div className="mt-6">
      <div className="space-y-2">
        <div className='flex flex-row items-center '>
          <div className="text-sm font-semibold text-unselected-text font-h4 mr-2 mt-2">{newNumber}</div>
          {isSetPhoneNumberLoading ? <Spinner variant={{ size: 'small' }} show={isSetPhoneNumberLoading} /> : (
            <span data-testid="phoneNumber-selectedNumber">
              <Typography
                variant={{ size: 'h5', colour: 'brand2' }}
              >
                {getPhoneNumberInFormat(currentNumber?.value)}
              </Typography>
            </span>
          )}
        </div>
        {isNumberLoading ? (
          <div className="mb-6 mt-2">
            <Spinner variant={{ size: 'small' }} show={isNumberLoading} />
          </div>
        ) : (
          !showMore && (
            <div className="mb-4 create-new-number-radios">
              <Input
                type="radio"
                name="phoneNumber-newNumber"
                options={phoneNumberOptions}
                value={selectedPhoneNumberValue}
                onChange={handlePhoneNumberChange}
              />
            </div>
          )
        )}
        {!currentNumber ? (
          <div className='mt-1 [&>*>*]:!min-h-0 items-center'>
            <CardInfo
              testId="noNumber-Error"
              tokens={{
                backgroundColor: '#fcd3c6',
              }}
            >
              <div className="flex items-center">
                <div className="mt-[2px]">
                  <Icon
                    accessibilityLabel="Error"
                    icon="ExclamationOctagon"
                    variant={{ color: 'danger', size: 'small' }}
                    tokens={{
                      color: '#C12335'
                    }}
                  />
                </div>
                <div className="ml-4">
                  <Typography>
                    {noNumber}
                  </Typography>
                </div>
              </div>
            </CardInfo>
          </div>
        ) : (
          <>
            {reservedError && <BodyError>{reservedError}</BodyError>}
            <div className='flex flex-row flex-nowrap items-end gap-2'>
              <div className='mb-[3px]'>
                <Icon
                  accessibilityLabel="Refresh"
                  icon="Refresh"
                  testId='refresh'
                  variant={{ size: 'micro' }}
                />
              </div>
              <Link
                href="#"
                onClick={handleSeeMoreClick}
                onKeyDown={handleSeeMoreKeyDown}
                testId="phoneNumber-seeMore"
                className="whitespace-nowrap"
              >
                {showMore ? seeMore : refresh}
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default CreateNewNumber
